import { useId } from "react"

// Shared analytics chart primitives, used by both the account-wide rollup
// (AccountAnalyticsPage) and the per-code report (CodeAnalyticsPage). All
// charts are hand-rolled SVG/DOM — voltage fills on neutral surfaces, per
// brand; no charting library (see the analytics design spec). Every numeric
// value renders in JetBrains Mono (font-mono), per the UI rules.
//
// Visual values map onto the existing Tailwind theme / brand tokens
// (bg-voltage, text-coal, rounded-lg, font-display, --color-*) — nothing here
// invents a value.

// ---- Icons (Lucide paths, stroke currentColor, width 2) ---------------
//
// The codebase inlines Lucide SVGs per component rather than depending on an
// icon package; this is the small shared set the analytics surfaces need.
const ICON_PATHS = {
  lock: <><rect x="3" y="11" width="18" height="11" rx="2" ry="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></>,
  trendingUp: <><polyline points="22 7 13.5 15.5 8.5 10.5 2 17" /><polyline points="16 7 22 7 22 13" /></>,
  trendingDown: <><polyline points="22 17 13.5 8.5 8.5 13.5 2 7" /><polyline points="16 17 22 17 22 11" /></>,
  arrowRight: <><line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" /></>,
  arrowLeft: <><line x1="19" y1="12" x2="5" y2="12" /><polyline points="12 19 5 12 12 5" /></>,
  sparkles: <><path d="M12 3l1.9 5.8L19.5 11l-5.6 2.2L12 19l-1.9-5.8L4.5 11l5.6-2.2z" /></>,
  // Added for the locked page's copy/download row — the two actions that stay
  // free on every plan. Same Lucide glyphs CodeActions and HeroResult inline.
  copy: <><rect x="9" y="9" width="13" height="13" rx="2" ry="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" /></>,
  check: <><polyline points="20 6 9 17 4 12" /></>,
  download: <><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></>,
}

export function Icon({ name, size = 16, className = "" }) {
  return (
    <svg
      width={size} height={size} viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
      aria-hidden="true" className={className}
    >
      {ICON_PATHS[name]}
    </svg>
  )
}

// ---- Formatting helpers -----------------------------------------------

export const fmt = (n) => (n || 0).toLocaleString()

// Regional-indicator emoji flag for a 2-letter ISO country code. Returns a
// neutral globe for null/unknown codes so the "Unknown" row still gets a glyph.
export function flagFor(code) {
  if (!code || code.length !== 2 || !/^[A-Za-z]{2}$/.test(code)) return "🌐"
  const base = 0x1f1e6
  return String.fromCodePoint(
    base + (code.toUpperCase().charCodeAt(0) - 65),
    base + (code.toUpperCase().charCodeAt(1) - 65),
  )
}

// Percent change of `now` vs `prev`. Mirrors the dashboard trend pill: 100%
// when growing from zero, 0% when both are zero.
export function pct(now, prev) {
  if (!prev) return now ? 100 : 0
  return Math.round(((now - prev) / prev) * 100)
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"]

// Format a "YYYY-MM-DD" string as "May 3". Parsed by parts (not new Date) so
// the label never drifts a day under a non-UTC local timezone.
function fmtDayLabel(iso) {
  const [, m, d] = iso.split("-").map(Number)
  return `${MONTHS[m - 1]} ${d}`
}

// Five evenly-spaced x-axis ticks for an oldest→newest day series, with the
// final tick always reading "Today" (the series ends on the current UTC day).
export function axisLabels(points) {
  if (!points || points.length === 0) return []
  const n = points.length
  const ticks = 4
  const out = []
  for (let i = 0; i <= ticks; i++) {
    if (i === ticks) { out.push("Today"); continue }
    out.push(fmtDayLabel(points[Math.round((n - 1) * (i / ticks))].date))
  }
  return out
}

// Compact relative time ("just now" / "5m ago" / "2h ago" / "3d ago") for the
// live last-scan pulse. Returns null for a missing timestamp.
export function timeAgo(iso) {
  if (!iso) return null
  const then = new Date(iso).getTime()
  if (Number.isNaN(then)) return null
  const secs = Math.max(0, Math.round((Date.now() - then) / 1000))
  if (secs < 60) return "just now"
  const mins = Math.round(secs / 60)
  if (mins < 60) return `${mins}m ago`
  const hours = Math.round(mins / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.round(hours / 24)
  if (days < 30) return `${days}d ago`
  const months = Math.round(days / 30)
  if (months < 12) return `${months}mo ago`
  return `${Math.round(months / 12)}y ago`
}

export const DEVICE_LABELS = {
  mobile:  "Mobile",
  desktop: "Desktop",
  tablet:  "Tablet",
  bot:     "Bots",
  unknown: "Unknown",
}

// Bot hits are in the device breakdown and nowhere else (#318): every other
// figure counts scans only. Say so under the bars whenever there are any, or
// the bars add up to more than the total.
export function BotNote({ rows }) {
  if (!rows?.some((r) => r.class === "bot" && r.scans > 0)) return null
  return (
    <p className="mt-3 text-xs text-[color:var(--color-muted)] dark:text-muted-light">
      Bots are link previews, crawlers and scripts. They are not counted as scans.
    </p>
  )
}

// Map the report's device buckets to BarRows rows, dropping empty buckets.
export function deviceRows(rows) {
  return rows
    .filter((r) => r.scans > 0)
    .map((r) => ({ key: r.class, label: DEVICE_LABELS[r.class] || r.class, scans: r.scans }))
}

// Map the report's country buckets to BarRows rows with flag glyphs. `limit`
// caps the visible rows; `withOther` folds the long tail into a trailing
// "Other" aggregate (the per-code page wants it, the account leaderboard-style
// top-6 doesn't).
export function countryRows(rows, { limit = 5, withOther = false } = {}) {
  const shown = rows.slice(0, limit).map((r) => ({
    key:   r.code ?? "unknown",
    label: r.name || r.code || "Unknown",
    badge: flagFor(r.code),
    scans: r.scans,
  }))
  if (withOther) {
    const rest = rows.slice(limit).reduce((s, r) => s + r.scans, 0)
    if (rest > 0) shown.push({ key: "__other", label: "Other", scans: rest })
  }
  return shown
}

// ---- Reusable card chrome ---------------------------------------------

function Eyebrow({ children }) {
  return (
    <span className="text-xs uppercase tracking-wider text-[color:var(--color-muted)] dark:text-muted-light">
      {children}
    </span>
  )
}

// Titled chart card: an eyebrow label, an optional right-aligned mono metric,
// then the chart body.
export function ChartCard({ title, right, children, className = "" }) {
  return (
    <div className={"bg-white dark:bg-midnight border border-[color:var(--color-border)] dark:border-storm rounded-lg shadow-sm p-4 " + className}>
      <div className="flex items-center justify-between mb-3">
        <Eyebrow>{title}</Eyebrow>
        {right}
      </div>
      {children}
    </div>
  )
}

// ---- Headline KPI card -------------------------------------------------
//
// `emphasis` paints the card coal with a voltage-light value (the dashboard's
// hero stat treatment).
//
// There is deliberately no locked variant. This tile used to blur its value
// when gated, and briefly rendered an em dash once the server started
// withholding the number — but a row of dashes says "broken", not "locked".
// A gated surface drops the tile entirely and states what's behind the lock
// in LockedPanel instead, so every tile that renders carries a real number.
export function AnStat({ eyebrow, value, sub, trend, spark, emphasis = false }) {
  const up = trend == null ? null : trend >= 0
  const surface = emphasis
    ? "bg-coal text-cloud border-coal shadow-sm"
    : "bg-white dark:bg-midnight border-[color:var(--color-border)] dark:border-storm shadow-sm"
  const valueColor = emphasis ? "text-voltage-light" : "text-ink dark:text-cloud"
  const subColor = emphasis ? "text-cloud/60" : "text-[color:var(--color-muted)] dark:text-muted-light"

  return (
    <div className={"border rounded-lg p-4 relative overflow-hidden " + surface}>
      <Eyebrow>{eyebrow}</Eyebrow>
      <div className="flex items-end justify-between gap-2 mt-1.5">
        <div className={"font-display font-semibold text-2xl sm:text-3xl leading-none " + valueColor}>
          {value}
        </div>
        {spark && (
          <div className="flex-shrink-0 pb-0.5">
            <MiniSpark data={spark} color={emphasis ? "var(--color-voltage-light)" : "var(--color-voltage)"} />
          </div>
        )}
      </div>
      <div className="flex items-center gap-2 mt-2 text-xs">
        {trend != null && (
          <span className={"inline-flex items-center gap-1 font-mono " + (up ? "text-voltage-dark dark:text-voltage-light" : "text-fail")}>
            <Icon name={up ? "trendingUp" : "trendingDown"} size={13} />{up ? "+" : ""}{trend}%
          </span>
        )}
        {sub && <span className={subColor}>{sub}</span>}
      </div>
    </div>
  )
}

// Inline sparkline for the emphasized headline card.
export function MiniSpark({ data, color = "var(--color-voltage)", w = 72, h = 30 }) {
  const max = Math.max(1, ...data)
  const step = data.length > 1 ? w / (data.length - 1) : w
  const pts = data.map((v, i) => [i * step, h - (v / max) * (h - 4) - 2])
  const d = pts.map((p, i) => (i ? "L" : "M") + p[0].toFixed(1) + " " + p[1].toFixed(1)).join(" ")
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} style={{ display: "block", overflow: "visible" }}>
      <path d={d} fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

// ---- Big area chart: voltage line, 0→22% gradient fill, gridlines, dated x-axis ----
//
// `series` is oldest→newest day counts; `labels` are the x-axis ticks (mono,
// ending in "Today"). The gridline color rides currentColor so it switches in
// dark mode; the line + fill stay voltage in both themes.
export function AreaChart({ series, labels = [], height = 150 }) {
  const w = 760
  const h = height
  const peak = Math.max(1, ...series)
  const step = series.length > 1 ? w / (series.length - 1) : 0
  const coords = series.map((v, i) => [i * step, h - (v / peak) * (h - 16) - 6])
  const line = coords.map(([x, y]) => `${x.toFixed(1)},${y.toFixed(1)}`).join(" ")
  const fill = `${line} ${w},${h} 0,${h}`
  const gid = useId().replace(/[:]/g, "")
  const grid = [0.25, 0.5, 0.75]
  return (
    <div>
      <svg
        viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none"
        className="w-full text-[color:var(--color-border)] dark:text-storm"
        style={{ height }}
      >
        <defs>
          <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="var(--color-voltage)" stopOpacity="0.22" />
            <stop offset="100%" stopColor="var(--color-voltage)" stopOpacity="0" />
          </linearGradient>
        </defs>
        {grid.map((g) => (
          <line key={g} x1="0" x2={w} y1={h * g} y2={h * g} stroke="currentColor" strokeWidth="1" vectorEffect="non-scaling-stroke" />
        ))}
        <polyline points={fill} fill={`url(#${gid})`} stroke="none" />
        <polyline points={line} fill="none" stroke="var(--color-voltage)" strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
      </svg>
      {labels.length > 0 && (
        <div className="flex justify-between font-mono text-[10px] text-[color:var(--color-muted)] dark:text-muted-light mt-1.5">
          {labels.map((l, i) => <span key={i}>{l}</span>)}
        </div>
      )}
    </div>
  )
}

// ---- Horizontal bar list (country / device) --------------------------
//
// `rows` is [{ key, label, badge?, scans }]. `badge` is an optional leading
// glyph (a country flag emoji). An empty `rows` renders a measured hint.
//
// The label column is 40% of the row (at most 11rem) and a long label wraps
// rather than cuts: a fixed 6rem column cut "United States" to "United State"
// (#327).
export function BarRows({ rows, max, emptyHint = "No scans yet" }) {
  if (!rows || rows.length === 0) {
    return <div className="text-xs italic text-[color:var(--color-muted)] dark:text-muted-light py-2">{emptyHint}</div>
  }
  const peak = max || Math.max(1, ...rows.map((r) => r.scans))
  return (
    <div className="flex flex-col gap-2">
      {rows.map((r) => (
        <div key={r.key} className="flex items-center gap-2 text-sm">
          <span className="w-2/5 max-w-44 min-w-0 flex items-start gap-1.5 leading-snug text-ink dark:text-cloud">
            {r.badge && <span aria-hidden="true" className="flex-shrink-0">{r.badge}</span>}
            <span className="min-w-0 break-words">{r.label}</span>
          </span>
          <span className="flex-1 h-1.5 bg-[color:var(--color-cloud)] dark:bg-coal rounded-full overflow-hidden">
            <span
              className="block h-full bg-voltage rounded-full transition-[width] duration-[280ms] ease-[cubic-bezier(0.4,0,0.2,1)]"
              style={{ width: `${Math.max(2, (r.scans / peak) * 100)}%` }}
            />
          </span>
          <span className="w-12 flex-shrink-0 text-right font-mono text-xs text-[color:var(--color-muted)] dark:text-muted-light">{fmt(r.scans)}</span>
        </div>
      ))}
    </div>
  )
}

// ---- Time-of-day histogram (24 UTC buckets) --------------------------
export function Hours({ buckets }) {
  const peak = Math.max(1, ...buckets)
  return (
    <div>
      <div className="flex items-end gap-[2px] h-[116px]">
        {buckets.map((c, h) => (
          <div
            key={h}
            className="flex-1 bg-voltage/85 rounded-[1px] transition-[height] duration-[280ms] ease-[cubic-bezier(0.4,0,0.2,1)]"
            style={{ height: `${Math.max(3, (c / peak) * 100)}%` }}
            title={`${h}:00 · ${fmt(c)} scans`}
          />
        ))}
      </div>
      <div className="flex justify-between font-mono text-[10px] text-[color:var(--color-muted)] dark:text-muted-light mt-1.5">
        <span>0</span><span>6</span><span>12</span><span>18</span><span>24</span>
      </div>
    </div>
  )
}

// ---- Locked panel: what a gated surface renders instead of data -------
//
// Deliberately NOT a ghost of the unlocked page. The old treatment blurred
// the real charts, and the blur was doing real work: it carried shape and
// volume — "there's a lot here, and it's yours." Once the server started
// withholding those numbers there was nothing left to blur, and the first
// attempt (an empty axis frame plus em-dashed tiles) lost that signal
// entirely — it reads as a chart that failed to load, not as a locked one.
//
// So this says the same thing in words, and every word is true: how many
// scans are recorded, since when, and exactly what unlocking shows. The
// alternative considered and rejected was rendering plausible fake charts
// under a blur, which carries the volume signal fine right up until someone
// opens devtools and finds numbers we invented — on the one page whose whole
// pitch is that their data is real, complete, and still being collected.
//
// The fallback navigates to /pricing — a real page — matching ShapeSelect's
// idiom. It used to be `/app?upgrade=1`, a param no controller or component
// ever read, so an un-wired CTA silently dead-ended on the code list.
export function LockedPanel({
  title = "Your scan detail is saved and waiting",
  header,
  lead,
  items = [],
  itemsLabel = "Unlock to see",
  onUpgrade,
  busy = false,
  footnote = "Still recording. Nothing is missing.",
}) {
  const upgrade = onUpgrade || (() => window.location.assign("/pricing"))
  return (
    <div className="bg-white dark:bg-midnight border border-[color:var(--color-border)] dark:border-storm rounded-lg shadow-sm p-6 sm:p-8 text-center">
      {/* `header` carries the data the plan DOES entitle — on the per-code page,
          the surviving total. It lives inside this card rather than in its own
          tile above it: a narrow stat card stacked on a full-width panel reads
          as two unrelated objects, and these are one story ("here's what you
          have, here's what's behind the gate"). The rule stays divided by the
          hairline, not by a gap between cards. */}
      {header && (
        <>
          {header}
          <hr className="my-6 sm:my-7 border-0 border-t border-[color:var(--color-border)] dark:border-storm" />
        </>
      )}
      <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-plasma-pale text-plasma mb-3">
        <Icon name="lock" size={20} />
      </span>
      <div className="font-display font-semibold text-lg text-ink dark:text-cloud">{title}</div>
      {lead && (
        <p className="text-sm text-[color:var(--color-muted)] dark:text-muted-light mt-1.5 max-w-md mx-auto">{lead}</p>
      )}

      {items.length > 0 && (
        <>
          {/* Its own line, not trailing the lead sentence — as a sentence tail
              it orphaned onto a wrapped line directly above the list it
              introduces, which read like a typo. */}
          <div className="mt-5 text-xs uppercase tracking-wider text-[color:var(--color-muted)] dark:text-muted-light">
            {itemsLabel}
          </div>
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2 mt-2.5 max-w-md mx-auto text-left">
            {items.map((item) => (
              <li key={item} className="flex items-center gap-2 text-sm text-ink dark:text-cloud">
                <span className="w-1.5 h-1.5 rounded-full bg-voltage flex-shrink-0" aria-hidden="true" />
                {item}
              </li>
            ))}
          </ul>
        </>
      )}

      <button
        type="button"
        onClick={upgrade}
        disabled={busy}
        className="mt-6 inline-flex items-center gap-1.5 bg-plasma text-white rounded-lg px-5 py-2.5 font-semibold text-sm hover:bg-plasma-dark transition disabled:opacity-60 disabled:cursor-wait"
      >
        <Icon name="sparkles" size={15} />{busy ? "Opening checkout…" : "Upgrade to Pro"}
      </button>

      {footnote && (
        <p className="text-xs text-[color:var(--color-muted)] dark:text-muted-light mt-3">{footnote}</p>
      )}
    </div>
  )
}
